import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Siren } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const HOLD_MS = 2000;

/**
 * The duress alarm, reachable from every screen of the field app.
 *
 * ⚠️ HELD, not tapped, and deliberately so. A guard under threat cannot work a
 * confirmation dialog — two taps and a modal is a control they will not reach in
 * time. But a bare button in a phone's top bar fires in a pocket, and an alarm
 * that cries wolf stops being answered, which is the same failure arriving more
 * slowly. Holding for two seconds is the one gesture that is neither.
 *
 * ⚠️ It never blocks on the GPS. `getCurrentPosition` can hang for its full
 * timeout indoors — a basement car park is exactly where this button gets used —
 * so the alarm is sent the moment the hold completes and the position follows
 * only if it arrives first. A location is worth having; it is not worth waiting
 * eight seconds for while somebody is in trouble.
 */
export default function DuressButton() {
  const [held, setHeld] = useState(0);
  const [sent, setSent] = useState(false);
  const timer = useRef(null);
  const started = useRef(0);

  useEffect(() => () => clearInterval(timer.current), []);

  const fire = async () => {
    stop();
    setSent(true);
    // Race the fix against a short deadline; whichever wins, we send.
    const pos = await new Promise((res) => {
      let settled = false;
      const done = (v) => { if (!settled) { settled = true; res(v); } };
      setTimeout(() => done(null), 1500);
      if (!navigator.geolocation) return done(null);
      navigator.geolocation.getCurrentPosition(
        (p) => done({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => done(null),
        { timeout: 1500, enableHighAccuracy: true }
      );
    });
    try {
      const { data } = await api.post("/attendance/duress", {
        lat: pos?.lat ?? null, lng: pos?.lng ?? null,
      });
      toast.error(
        `ALARM SENT — ${data.ref_no}. ` +
        (data.located ? `Supervisors alerted for ${data.site_name}.` : "Supervisors alerted.") +
        (data.has_fix ? "" : " Your phone gave no location — say where you are if you can."),
        { duration: 15000 }
      );
    } catch (e) {
      // ⚠️ Never a bare "failed". The one thing a guard must be told is whether
      // anybody heard them, and if not, what to do instead.
      toast.error("ALARM DID NOT SEND — no connection. Call the duty office now; the number is on Profile.", { duration: 20000 });
    }
    setTimeout(() => setSent(false), 6000);
  };

  const stop = () => {
    clearInterval(timer.current);
    timer.current = null;
    setHeld(0);
  };

  const start = () => {
    if (timer.current || sent) return;
    started.current = Date.now();
    timer.current = setInterval(() => {
      const pct = Math.min(1, (Date.now() - started.current) / HOLD_MS);
      setHeld(pct);
      if (pct >= 1) fire();
    }, 50);
  };

  return (
    <button
      type="button"
      data-testid="duress-button"
      aria-label="Emergency — hold to raise a duress alarm"
      onPointerDown={start}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onContextMenu={(e) => e.preventDefault()}
      className="relative overflow-hidden h-8 px-2.5 flex items-center gap-1.5 border border-[hsl(var(--alert))] text-[hsl(var(--alert))] select-none touch-none"
      style={{ WebkitTouchCallout: "none" }}
    >
      <span
        aria-hidden
        className="absolute inset-0 bg-[hsl(var(--alert))] origin-left"
        style={{ transform: `scaleX(${held})`, transition: held ? "none" : "transform 150ms" }}
      />
      <Siren size={15} weight="fill" className={`relative ${held > 0 ? "text-white" : ""}`} />
      <span className={`relative font-mono text-[10px] font-bold tracking-wider ${held > 0 ? "text-white" : ""}`}>
        {sent ? "SENT" : held > 0 ? "HOLD…" : "SOS"}
      </span>
    </button>
  );
}
